import { useEffect, useState, useRef } from "react";

export function useAutoUpdate({ interval = 60000 } = {}) {
  const [isRecovering, setIsRecovering] = useState(false);
  const currentScriptSrcRef = useRef(null);

  // 1. Initialize: Capture the current main script src on mount
  useEffect(() => {
    const scripts = document.getElementsByTagName("script");
    for (let i = 0; i < scripts.length; i++) {
      const src = scripts[i].src;
      if (src && src.includes("/static/js/main.")) {
        currentScriptSrcRef.current = src;
        break;
      }
    }
  }, []);

  // 2. Recovery Logic
  const triggerRecovery = () => {
    if (isRecovering) return;

    console.warn(
      "[AutoUpdate] Update detected or ChunkLoadError, initiating recovery...",
    );
    setIsRecovering(true);

    // Wait 2 seconds before reloading to show the UI
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  // 3. ChunkLoadError Listener
  useEffect(() => {
    const handler = (e) => {
      if (
        e?.message?.includes("ChunkLoadError") ||
        e?.message?.includes("Failed to fetch dynamically imported module")
      ) {
        triggerRecovery();
      }
    };

    window.addEventListener("error", handler);
    return () => window.removeEventListener("error", handler);
  }, []);

  // 4. Version Check (Polling)
  useEffect(() => {
    // 取得 script 檔名（避免比對完整 URL 造成誤判）
    const getFileName = (src) => src?.split("/").pop();

    const checkVersion = async () => {
      // 若已進入 recovery，或尚未記錄目前版本，直接略過
      if (isRecovering || !currentScriptSrcRef.current) return;

      try {
        // 強制抓最新 index.html（避免瀏覽器快取）
        const response = await fetch(`/?t=${Date.now()}`);
        const html = await response.text();

        // 使用 DOMParser 解析 HTML，避免用字串 regex 出錯
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        // 從新的 index.html 中找出 main bundle 的 script
        const scripts = doc.getElementsByTagName("script");
        let newScriptSrc = null;

        for (let i = 0; i < scripts.length; i++) {
          const src = scripts[i].src;

          // CRA 預設 main bundle 命名規則
          if (src && src.includes("/static/js/main.")) {
            newScriptSrc = src;
            break;
          }
        }

        // 比對「檔名」是否不同（避免 domain / protocol 差異誤判）
        if (
          newScriptSrc &&
          getFileName(newScriptSrc) !== getFileName(currentScriptSrcRef.current)
        ) {
          console.log(`[AutoUpdate] Version mismatch detected.`, {
            current: currentScriptSrcRef.current,
            latest: newScriptSrc,
          });

          // 觸發 recovery（顯示提示 → 延遲 reload）
          triggerRecovery();
        }
      } catch (err) {
        // 檢查失敗不應中斷 App，只記錄錯誤
        console.error("[AutoUpdate] Failed to check version", err);
      }
    };

    // 🟢 App 啟動時立即檢查一次（避免等待 interval 空窗期）
    checkVersion();

    // 定期輪詢檢查版本
    const intervalId = setInterval(checkVersion, interval);

    // 使用者切回分頁時，再檢查一次（長時間掛著頁面）
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [interval, isRecovering]);

  return { isRecovering };
}
